import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// MUI Icons
import EditIcon from '@mui/icons-material/Edit';
import AddCommentIcon from '@mui/icons-material/AddComment';

// Custom Components
import DraggableDialog from '@/ui/Dialog/DraggableDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import useFormController from '@/components/useFormController';
import GridContainer from '@/ui/Grid/GridContainer';
import FormContent from '@/components/FormContent';

import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import DataController from '@/lib/DataController';
import modelUsers from '@/models/user';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItemDelete from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete';

const TenantAdminUsersDialog = (props: IDialogProps) => {
  const { mode, dataId, onClose, form, initialRecord, tenantId } = props;

  const { t } = useTranslation();
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);

  const dc = new DataController( modelUsers, `system/tenants/${String(tenantId)}/users` );

  const close = (result: any) => {
    if (onClose) {
      onClose(result);
    }

    hideDialog();
  };

  const {
    record,
    validation,
    fields,
    pickerDataControllers,
    onFieldChange,
    doInsert,
    doUpdate,
    doDelete,
  } = useFormController({
    mode,
    recordId: dataId,
    form,
    dc,
    onClose: close,
    initialRecord,
  });

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleClose = () => {
    close({ success: false });
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.insert_user_success';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert' });
          } else if (result.error) {
            const msg = 'error.insert_user';
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.update_user_success';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = 'error.update_user';
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleDelete = () => {
    doDelete()
      .then((result) => {
        if (result.success) {
          const msg = 'messages.delete_user_success';
          showNotification(msg, 'success');
          close({ dataChanged: true, action: 'delete' });
        }
      })
      .catch((resp) => {
        if (resp.error) {
          showNotification(resp.error, 'error');
        }
      });
  };

  return (
    <DraggableDialog open maxWidth="sm" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          {mode === 'update' ? (
            <EditIcon sx={{ transform: 'scale(1.3)' }} />
          ) : mode === 'insert' ? (
            <AddCommentIcon sx={{ transform: 'scale(1.3)' }} />
          ) : null}
          <DialogToolbarHeading>
            {mode === 'insert'
              ? t('admin:titles.add_user')
              : mode === 'update'
                ? t('admin:titles.edit_user')
                : null}
          </DialogToolbarHeading>
          <ToolbarFillContent />
          {mode === 'update' ? (
            <DialogToolbarMenuButton>
              <DialogToolbarMenuItemDelete onClick={handleDelete} />
            </DialogToolbarMenuButton>
          ) : null}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer direction="row" spacing={2} my={2}>
          <FormContent
            fieldNames={dc.getFormFieldsNames(form)}
            // @ts-ignore
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode={mode}
            columns={1}
            pickerDataControllers={pickerDataControllers}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DraggableDialog>
  );
};

export default TenantAdminUsersDialog;
